import {useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryResult} from '@tanstack/react-query';
import axios from 'axios';
import {httpDelete, httpGet, httpPatch, httpPost} from '../http.service';
import {QUERY_KEYS} from '../../utils/queryKeys';

const BASE = 'editor/roomSlugs';

export interface RoomSlugParam {
    key: string;
    value: string;
}

export interface RoomSlug {
    _id: string;
    slug: string;
    roomId: string;
    presenterCode?: string;
    params?: RoomSlugParam[];
    isFixed?: boolean;
    created?: string;
    lastModified?: string;
}

export interface CreateRoomSlugPayload {
    roomId: string;
    slug: string;
    presenterCode?: string;
    params?: RoomSlugParam[];
}

export interface CreateAutoRoomSlugPayload {
    roomId: string;
    presenterCode?: string;
    params?: RoomSlugParam[];
}

export interface UpdateRoomSlugPayload {
    slug?: string;
    params?: RoomSlugParam[];
}

export function isRoomSlugConflictError(error: unknown): boolean {
    return axios.isAxiosError(error) && error.response?.status === 409;
}

export function useListRoomSlugs(roomId: string): UseQueryResult<RoomSlug[]> {
    return useQuery(QUERY_KEYS.roomSlug.byRoom(roomId), () => httpGet<RoomSlug[]>(`${BASE}/${roomId}`), {
        enabled: !!roomId,
    });
}

export function useCreateRoomSlug(): UseMutationResult<RoomSlug, unknown, CreateRoomSlugPayload> {
    const queryClient = useQueryClient();
    return useMutation((payload: CreateRoomSlugPayload) => httpPost<RoomSlug>(BASE, payload), {
        onSuccess: () => {
            queryClient.invalidateQueries(QUERY_KEYS.roomSlug.all);
        },
    });
}

export function useCreateAutoRoomSlug(): UseMutationResult<RoomSlug, unknown, CreateAutoRoomSlugPayload> {
    const queryClient = useQueryClient();
    return useMutation((payload: CreateAutoRoomSlugPayload) => httpPost<RoomSlug>(`${BASE}/auto`, payload), {
        onSuccess: () => {
            queryClient.invalidateQueries(QUERY_KEYS.roomSlug.all);
        },
    });
}

export function useUpdateRoomSlug(slugId: string): UseMutationResult<RoomSlug, unknown, UpdateRoomSlugPayload> {
    const queryClient = useQueryClient();
    return useMutation((payload: UpdateRoomSlugPayload) => httpPatch<RoomSlug>(`${BASE}/${slugId}`, payload), {
        onSuccess: () => {
            queryClient.invalidateQueries(QUERY_KEYS.roomSlug.all);
        },
    });
}

export function useDeleteRoomSlug(): UseMutationResult<void, unknown, string> {
    const queryClient = useQueryClient();
    return useMutation((slugId: string) => httpDelete<void>(`${BASE}/${slugId}`, {}), {
        onSuccess: () => {
            queryClient.invalidateQueries(QUERY_KEYS.roomSlug.all);
        },
    });
}

export function useCheckRoomSlugAvailable(slug: string, excludeId?: string): UseQueryResult<boolean> {
    return useQuery(
        QUERY_KEYS.roomSlug.check(slug, excludeId),
        () =>
            httpGet<{available: boolean}>(`${BASE}/check`, {params: {slug, ...(excludeId ? {excludeId} : {})}}).then(
                (res) => res.available
            ),
        {
            enabled: !!slug,
            staleTime: 0,
            cacheTime: 0,
            retry: false,
            refetchOnMount: 'always',
            refetchOnWindowFocus: true,
        }
    );
}
